import * as THREE from "three";
import { setTerrainHeightProvider } from "./terrain";
import { paintFarFieldVertexColors } from "./terrain-materials";
import { addHorizonRing } from "./horizon";
import { GREEN_HAMMER_ROUTE } from "./nav";

// GREEN HAMMER (map04) — Theater 2 in THEME.md: Central American jungle at
// pre-dawn. Rolling hills under triple canopy, a river with one ford, a
// grove village, a cleared border strip past the LZ. The canopy carries the
// read from orbit — thousands of instanced crown blobs with clearings where
// the fiction needs open ground. Stylized-only, born after the direction
// locked. Canopy is dressing: losTransparent (the FLIR cuts through it) and
// no ballistics, so the fight stays legible underneath.

const ROUTE = GREEN_HAMMER_ROUTE.map((n) => n.position);

// The river runs NW→SE and crosses the route exactly at THE FORD.
const RIVER: Array<[number, number]> = [
  [-460, 220],
  [-260, 80],
  [-80, -60],
  [80, -240],
  [280, -470],
];
const FORD: [number, number] = [-80, -60];
export const WATER_LEVEL = -0.55;

// Clearings the canopy respects (and the paint brightens): the landing, the
// grove village, the LZ. The border strip is handled separately.
const CLEARINGS: Array<[number, number, number]> = [
  [-380, -330, 80], // RIVER LANDING
  [60, 60, 95], // BANANA GROVE
  [350, 300, 90], // BORDER LZ
];

function smoothNoise(x: number, z: number, scale: number): number {
  return (
    Math.sin(x * scale) * 0.42 +
    Math.cos(z * scale * 1.17) * 0.36 +
    Math.sin((x + z) * scale * 0.63) * 0.22
  ) * 0.5 + 0.5;
}

function distanceToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  return Math.hypot(px - (ax + abx * t), pz - (az + abz * t));
}

function distanceToPolyline(x: number, z: number, points: Array<[number, number]>): number {
  let best = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    best = Math.min(best, distanceToSegment(x, z, a[0], a[1], b[0], b[1]));
  }
  return best;
}

export function map04RouteDistanceAt(x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i < ROUTE.length - 1; i++) {
    const a = ROUTE[i]!;
    const b = ROUTE[i + 1]!;
    best = Math.min(best, distanceToSegment(x, z, a.x, a.z, b.x, b.z));
  }
  return best;
}

export function map04RiverDistanceAt(x: number, z: number): number {
  return distanceToPolyline(x, z, RIVER);
}

// The cleared border strip: a straight diagonal band just past the LZ —
// the crossing GREEN HAMMER's fiction drives toward.
export function borderStripAt(x: number, z: number): number {
  // Signed distance to the strip centerline x + z = 760 (LZ sits at 650)
  return Math.abs(x + z - 760) / Math.SQRT2;
}

// Rolling jungle hills, ridges climbing at the edges, the route corridor
// graded into walkable lowland, the river carved below water level (shallow
// at the ford — the squad wades it).
export function map04HeightAt(x: number, z: number): number {
  const centerDist = Math.hypot(x, z);
  let height = 4
    + (smoothNoise(x + 40, z - 80, 0.004) - 0.5) * 30
    + (smoothNoise(x - 130, z + 60, 0.011) - 0.5) * 9
    + THREE.MathUtils.smoothstep(centerDist, 420, 1000) * (55 + smoothNoise(x, z, 0.0035) * 75);

  // The track is a graded corridor through the hills
  const routeDist = map04RouteDistanceAt(x, z);
  const corridor = 1 - THREE.MathUtils.smoothstep(routeDist, 22, 95);
  height = THREE.MathUtils.lerp(height, 2.5 + (smoothNoise(x, z, 0.02) - 0.5) * 2.4, corridor);

  // River carve — gentled near the ford so the crossing reads shallow
  const riverDist = map04RiverDistanceAt(x, z);
  const fordDist = Math.hypot(x - FORD[0], z - FORD[1]);
  const fordRelief = THREE.MathUtils.smoothstep(fordDist, 24, 90); // 0 at ford → 1 away
  const carve = (1 - THREE.MathUtils.smoothstep(riverDist, 7, 34)) * (0.45 + fordRelief * 0.55);
  height = THREE.MathUtils.lerp(height, -2.6, carve);

  // Border strip is bulldozed genuinely flat — the smoothstep shoulder does
  // the blending into the hills
  const strip = 1 - THREE.MathUtils.smoothstep(borderStripAt(x, z), 26, 60);
  height = THREE.MathUtils.lerp(height, 3.0, strip * 0.97);

  const edgeDist = Math.max(Math.abs(x), Math.abs(z));
  const edgeFade = 1 - THREE.MathUtils.smoothstep(edgeDist, 960, 1090);
  return THREE.MathUtils.lerp(-2, height, edgeFade);
}

function clearingMaskAt(x: number, z: number): number {
  let mask = 0;
  for (const [cx, cz, r] of CLEARINGS) {
    const d = Math.hypot(x - cx, z - cz);
    mask = Math.max(mask, 1 - THREE.MathUtils.smoothstep(d, r * 0.55, r));
  }
  return mask;
}

// Palette: greens on greens, kept deep — the misty pre-dawn key is pale and
// will lift everything (the valley/city lesson inverted: dim light needs
// value contrast baked into the bands, not luminance headroom).
const JUNGLE_FLOOR = new THREE.Color(0x40592b);
const JUNGLE_DEEP = new THREE.Color(0x374f26);
const JUNGLE_HIGH = new THREE.Color(0x2b4120);
const RIDGE_ROCK = new THREE.Color(0x5c5f52);
const CLEARING_GRASS = new THREE.Color(0x76854a);
const RIVER_MUD = new THREE.Color(0x6e6448);
const TRACK_DIRT = new THREE.Color(0x77684c);
const STRIP_EARTH = new THREE.Color(0x8a7a58);

function paintMap04Ground(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const color = new THREE.Color();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const h = pos.getY(i);
    const grain = smoothNoise(x, z, 0.028);

    // Altitude bands: floor greens deepen as the hills climb, rock breaks
    // through on the high ridges
    color.copy(JUNGLE_FLOOR).lerp(JUNGLE_DEEP, THREE.MathUtils.smoothstep(h, 14, 48));
    color.lerp(JUNGLE_HIGH, THREE.MathUtils.smoothstep(h, 48, 95));
    color.lerp(RIDGE_ROCK, THREE.MathUtils.smoothstep(h, 95, 140) * (0.35 + grain * 0.45));
    color.lerp(CLEARING_GRASS, grain * 0.12);

    // Clearings read brighter — worked ground
    const clearing = clearingMaskAt(x, z);
    if (clearing > 0) color.lerp(CLEARING_GRASS, clearing * 0.75);

    // Riverbanks are mud
    const riverDist = map04RiverDistanceAt(x, z);
    const bank = 1 - THREE.MathUtils.smoothstep(riverDist, 10, 42);
    if (bank > 0) color.lerp(RIVER_MUD, bank * 0.8);

    // The track
    const routeDist = map04RouteDistanceAt(x, z);
    const track = 1 - THREE.MathUtils.smoothstep(routeDist, 4, 10);
    if (track > 0) color.lerp(TRACK_DIRT, track * 0.85);

    // The bulldozed border strip
    const strip = 1 - THREE.MathUtils.smoothstep(borderStripAt(x, z), 24, 52);
    if (strip > 0) color.lerp(STRIP_EARTH, strip * 0.85);

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

const GROUND_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  flatShading: true,
  roughness: 1.0,
  metalness: 0,
});

// --- River water ------------------------------------------------------------
// Solid-color ribbon following the river polyline at WATER_LEVEL, wide
// enough to fill the carve. Same approach as map02's water: no shader, the
// stylized read is a flat dark band.
function makeRiverWater(): THREE.Mesh {
  const width = 46;
  const points: THREE.Vector3[] = [];
  for (let i = 0; i < RIVER.length - 1; i++) {
    const a = RIVER[i]!;
    const b = RIVER[i + 1]!;
    const segments = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / 18));
    for (let s = 0; s < segments; s++) {
      const t = s / segments;
      points.push(new THREE.Vector3(a[0] + (b[0] - a[0]) * t, 0, a[1] + (b[1] - a[1]) * t));
    }
  }
  points.push(new THREE.Vector3(RIVER[RIVER.length - 1]![0], 0, RIVER[RIVER.length - 1]![1]));

  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    positions.push(p.x - right.x * width * 0.5, WATER_LEVEL, p.z - right.z * width * 0.5);
    positions.push(p.x + right.x * width * 0.5, WATER_LEVEL, p.z + right.z * width * 0.5);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const water = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
    color: 0x2f4a42,
    roughness: 0.55,
    metalness: 0,
  }));
  water.name = "map04-river";
  water.receiveShadow = true;
  water.userData["losTransparent"] = true;
  water.userData["ignoreBallistics"] = true;
  return water;
}

const TRACK_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0x6f6148,
  roughness: 0.98,
  metalness: 0,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -2,
});

function resamplePolyline(points: THREE.Vector3[], step: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const segments = Math.max(1, Math.ceil(a.distanceTo(b) / step));
    for (let s = 0; s < segments; s++) out.push(a.clone().lerp(b, s / segments));
  }
  out.push(points[points.length - 1]!.clone());
  return out;
}

// Upward-wound ground ribbon (the hard-learned winding: base, base+2, base+1)
function makeTrackRibbon(points: THREE.Vector3[], width: number): THREE.Mesh {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    const lx = p.x - right.x * width * 0.5;
    const lz = p.z - right.z * width * 0.5;
    const rx = p.x + right.x * width * 0.5;
    const rz = p.z + right.z * width * 0.5;
    positions.push(lx, map04HeightAt(lx, lz) + 0.16, lz);
    positions.push(rx, map04HeightAt(rx, rz) + 0.16, rz);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const track = new THREE.Mesh(geo, TRACK_MATERIAL);
  track.name = "map04-track";
  track.receiveShadow = true;
  track.userData["losTransparent"] = true;
  return track;
}

// --- Canopy -----------------------------------------------------------------
// The jungle IS the canopy: dense instanced crown blobs everywhere the
// ground isn't worked (track, river, clearings, border strip), with taller
// emergent crowns punching through sparsely. Deterministic, two draw calls.

function scatterRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return (s >>> 0) / 0xffffffff;
  };
}

function canopyOpenAt(x: number, z: number): boolean {
  if (map04RouteDistanceAt(x, z) < 22) return true;
  if (map04RiverDistanceAt(x, z) < 30) return true;
  if (borderStripAt(x, z) < 52) return true;
  if (clearingMaskAt(x, z) > 0.25) return true;
  return false;
}

type CanopyRule = {
  count: number;
  colorA: THREE.Color;
  colorB: THREE.Color;
  radius: (rnd: () => number) => number;
  lift: number; // crown center height above ground, in crown radii
  squash: number; // vertical scale of the crown blob
  accept?: (x: number, z: number, h: number) => boolean;
};

function buildCanopy(name: string, geo: THREE.BufferGeometry, rule: CanopyRule, seed: number): THREE.InstancedMesh {
  const material = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.95, metalness: 0 });
  const mesh = new THREE.InstancedMesh(geo, material, rule.count);
  const rnd = scatterRng(seed);
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const color = new THREE.Color();
  let placed = 0;
  let attempts = 0;
  while (placed < rule.count && attempts < rule.count * 40) {
    attempts++;
    const x = (rnd() - 0.5) * 2040;
    const z = (rnd() - 0.5) * 2040;
    if (Math.max(Math.abs(x), Math.abs(z)) > 920) continue;
    if (canopyOpenAt(x, z)) continue;
    const h = map04HeightAt(x, z);
    if (h < 0.4) continue; // no trees in the water
    if (rule.accept && !rule.accept(x, z, h)) continue;
    const r = rule.radius(rnd);
    quat.setFromAxisAngle(up, rnd() * Math.PI * 2);
    matrix.compose(
      new THREE.Vector3(x, h + r * rule.lift, z),
      quat,
      new THREE.Vector3(r, r * rule.squash, r),
    );
    mesh.setMatrixAt(placed, matrix);
    color.copy(rule.colorA).lerp(rule.colorB, rnd());
    mesh.setColorAt(placed, color);
    placed++;
  }
  mesh.count = placed;
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  mesh.userData["losTransparent"] = true;
  mesh.userData["ignoreBallistics"] = true;
  return mesh;
}

function addCanopy(parent: THREE.Group): void {
  // Broadleaf crowns: the carpet
  parent.add(buildCanopy("map04-canopy", new THREE.IcosahedronGeometry(1, 1), {
    count: 7600,
    colorA: new THREE.Color(0x2a4318),
    colorB: new THREE.Color(0x436024),
    radius: (rnd) => 6 + rnd() * 6.5,
    lift: 1.15,
    squash: 0.62,
  }, 0x6a7b19));

  // Emergents: taller, darker crowns punching through the carpet
  parent.add(buildCanopy("map04-emergents", new THREE.ConeGeometry(1, 2.4, 6), {
    count: 520,
    colorA: new THREE.Color(0x1f3314),
    colorB: new THREE.Color(0x2e4a22),
    radius: (rnd) => 5 + rnd() * 3.5,
    lift: 2.1,
    squash: 1.0,
  }, 0x19d7a2));
}

// --- Villages ---------------------------------------------------------------
// Small stilt-hut clusters: the fishing hamlet at the landing, the grove
// village at BANANA GROVE, and a far-bank hamlet across the river (REDFOR
// country flavor). Simple massing — box on posts under a pitched roof.

const HUT_WALL = new THREE.MeshStandardMaterial({ color: 0x84714f, flatShading: true, roughness: 0.95 });
const HUT_ROOF = new THREE.MeshStandardMaterial({ color: 0x5e6637, flatShading: true, roughness: 1.0 });

function addHut(parent: THREE.Group, x: number, z: number, rot: number, rnd: () => number): void {
  const w = 7 + rnd() * 4;
  const d = 6 + rnd() * 3;
  const hutH = 3.2;
  const hut = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, hutH, d), HUT_WALL);
  body.position.y = 1.1 + hutH / 2; // on short stilts
  const roof = new THREE.Mesh(new THREE.ConeGeometry(Math.max(w, d) * 0.78, 2.6, 4), HUT_ROOF);
  roof.position.y = 1.1 + hutH + 1.3;
  roof.rotation.y = Math.PI / 4;
  hut.add(body, roof);
  hut.position.set(x, map04HeightAt(x, z), z);
  hut.rotation.y = rot;
  hut.children.forEach((c) => {
    c.castShadow = true;
    c.receiveShadow = true;
  });
  parent.add(hut);
}

function addVillages(parent: THREE.Group): void {
  const rnd = scatterRng(0x9a11a4);
  const clusters: Array<{ cx: number; cz: number; n: number; r: number }> = [
    { cx: -390, cz: -345, n: 5, r: 46 }, // fishing hamlet at the landing
    { cx: 62, cz: 68, n: 8, r: 62 }, // BANANA GROVE
    { cx: -230, cz: 150, n: 4, r: 40 }, // far-bank hamlet (across the river)
  ];
  for (const c of clusters) {
    for (let i = 0; i < c.n; i++) {
      const angle = rnd() * Math.PI * 2;
      const dist = 14 + rnd() * c.r;
      const x = c.cx + Math.cos(angle) * dist;
      const z = c.cz + Math.sin(angle) * dist;
      if (map04RouteDistanceAt(x, z) < 14 || map04RiverDistanceAt(x, z) < 16) continue;
      addHut(parent, x, z, rnd() * Math.PI * 2, rnd);
    }
  }
}

// Border fence posts along the strip — a thin broken line that sells "the
// border" from altitude without geometry cost.
function addBorderFence(parent: THREE.Group): void {
  const mat = new THREE.MeshStandardMaterial({ color: 0x4c4c46, flatShading: true, roughness: 0.9 });
  const post = new THREE.BoxGeometry(0.8, 6, 0.8);
  const mesh = new THREE.InstancedMesh(post, mat, 90);
  const matrix = new THREE.Matrix4();
  let placed = 0;
  for (let t = -640; t <= 640 && placed < 90; t += 15) {
    // Strip centerline: x + z = 760, direction (1,-1)/√2
    const x = 380 + t / Math.SQRT2;
    const z = 380 - t / Math.SQRT2;
    if (Math.max(Math.abs(x), Math.abs(z)) > 900) continue;
    matrix.setPosition(x, map04HeightAt(x, z) + 2.6, z);
    mesh.setMatrixAt(placed, matrix);
    placed++;
  }
  mesh.count = placed;
  mesh.name = "map04-border-fence";
  mesh.castShadow = true;
  mesh.frustumCulled = false;
  mesh.userData["losTransparent"] = true;
  mesh.userData["ignoreBallistics"] = true;
  parent.add(mesh);
}

export class Map04Terrain {
  readonly group = new THREE.Group();

  constructor(size = 2200) {
    setTerrainHeightProvider(map04HeightAt);
    const geo = new THREE.PlaneGeometry(size, size, 260, 260);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes["position"] as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, map04HeightAt(pos.getX(i), pos.getZ(i)));
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    paintMap04Ground(geo);
    const ground = new THREE.Mesh(geo, GROUND_MATERIAL);
    ground.name = "map04-ground";
    ground.receiveShadow = true;
    this.group.add(ground);

    this.group.add(makeRiverWater());
    this.group.add(makeTrackRibbon(resamplePolyline(ROUTE.map((v) => v.clone()), 12), 10));
    addCanopy(this.group);
    addVillages(this.group);
    addBorderFence(this.group);
    this.addFarField(size);
    // Mist-drowned horizon in deep greens
    addHorizonRing(this.group, size, { ridge: 0x3d4b38, haze: 0xa9b8a6 }, 0x14a04b7);
  }

  private addFarField(size: number): void {
    const geo = new THREE.CircleGeometry(size * 1.7, 96);
    geo.rotateX(-Math.PI / 2);
    paintFarFieldVertexColors(geo, size * 0.5, { inner: 0x3f5230, outer: 0x53663c, dry: 0x475a33 });
    const disc = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 }),
    );
    disc.name = "map04-farfield";
    disc.position.y = -2;
    disc.receiveShadow = true;
    disc.userData["losTransparent"] = true;
    this.group.add(disc);
  }
}
